import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { OrgInfoOut as OrgInfo } from '../types/apiRequests';
import { useBranding } from '../contexts/BrandingContext';
import { useTranslation } from '../i18n/useTranslation';

interface Props {
  orgInfo: OrgInfo;
  requestingTier: string | null;
  requestedTier: string | null;
  upgradeError: string | null;
  onRequestTier: (tier: string) => void;
}

export default function UpgradeBanner({ orgInfo, requestingTier, requestedTier, upgradeError, onRequestTier }: Props) {
  const t = useTranslation();
  const branding = useBranding();
  const portalTitle = branding.portal_title || 'MCP Hub';
  const bannerText = orgInfo.hub_tier === 'team'
    ? t('upgradeBanner.textTeam', { title: portalTitle })
    : t('upgradeBanner.text');
  return (
    <Box
      sx={{
        mb: 4,
        p: 3,
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '12px',
        bgcolor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 3,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <RocketLaunchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        </Box>
        <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
          {bannerText}
        </Typography>
      </Box>
      {(requestedTier || orgInfo.pending_platform_request) ? (
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontStyle: 'italic', flexShrink: 0 }}>
          {t('upgradeBanner.pending')}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, flexShrink: 0 }}>
        {upgradeError && (
          <Typography sx={{ fontSize: '0.8rem', color: 'error.main' }}>
            {upgradeError}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {orgInfo.hub_tier === 'free' && (
            <Button
              variant="outlined"
              size="small"
              disabled={requestingTier === 'team'}
              onClick={() => onRequestTier('team')}
              sx={{
                borderColor: 'rgba(0,0,0,0.15)',
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '0.8rem',
                borderRadius: '8px',
                textTransform: 'none',
                px: 2.5,
                '&:hover': { borderColor: 'text.primary', color: 'text.primary', bgcolor: 'transparent' },
              }}
            >
              {t('upgradeBanner.upgradeTeamButton')}
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            disabled={requestingTier === 'enterprise'}
            onClick={() => onRequestTier('enterprise')}
            sx={{
              bgcolor: 'primary.main',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.8rem',
              borderRadius: '8px',
              textTransform: 'none',
              px: 2.5,
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            {t('upgradeBanner.upgradeEnterpriseButton')}
          </Button>
        </Box>
        </Box>
      )}
    </Box>
  );
}
